/**
 * AI Funnel Client - Centralized AI access through GraphQL
 *
 * All AI requests go through database.teknology.eu/graphql → AI Orchestrator
 * This ensures:
 * - API keys are kept server-side only
 * - All requests are sanitized for PII
 * - Consistent model selection and fallback
 * - Centralized logging and monitoring
 */

import { graphqlQuery } from '../database/graphqlClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('AIFunnelClient');

// Types matching backend GraphQL schema
export interface AiChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AiChatInput {
  messages: AiChatMessage[];
  model?: string;
  temperature?: number;
  maxTokens?: number;
}

export interface AiChatResponse {
  success: boolean;
  content: string;
  modelUsed?: string;
  provider?: string;
  tokensUsed?: number;
  piiDetected: string[];
  error?: string;
}

export interface AiHealthResponse {
  available: boolean;
  provider: string;
  models: string[];
}

// For compatibility with existing code expecting Groq-like response
export interface AiChatResponseLegacy {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: {
    index: number;
    message: AiChatMessage;
    finish_reason: string;
  }[];
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * Check if AI service is available
 */
export async function checkAiHealth(): Promise<AiHealthResponse> {
  try {
    const data = await graphqlQuery<{ aiHealth: AiHealthResponse }>(`
      query {
        aiHealth {
          available
          provider
          models
        }
      }
    `);

    if (data?.aiHealth) {
      logger.info('AI health check passed', { provider: data.aiHealth.provider });
      return data.aiHealth;
    }

    return { available: false, provider: 'none', models: [] };
  } catch (error) {
    logger.error('AI health check failed', error);
    return { available: false, provider: 'none', models: [] };
  }
}

/**
 * Chat with AI through the centralized funnel
 */
export async function aiChat(
  messages: AiChatMessage[],
  options?: {
    model?: string;
    temperature?: number;
    maxTokens?: number;
  }
): Promise<AiChatResponse> {
  logger.debug('Starting AI chat request', { messageCount: messages.length });

  const input: AiChatInput = {
    messages,
    model: options?.model,
    temperature: options?.temperature,
    maxTokens: options?.maxTokens,
  };

  const data = await graphqlQuery<{ aiChat: AiChatResponse }>(`
    query AiChat($input: AiChatInput!) {
      aiChat(input: $input) {
        success
        content
        modelUsed
        provider
        tokensUsed
        piiDetected
        error
      }
    }
  `, { input });

  if (!data?.aiChat) {
    throw new Error('AI chat failed: no response from server');
  }

  if (!data.aiChat.success) {
    throw new Error(data.aiChat.error || 'AI chat failed');
  }

  logger.info('AI chat completed', {
    model: data.aiChat.modelUsed,
    provider: data.aiChat.provider
  });

  return data.aiChat;
}

/**
 * Chat with AI - Legacy format for backward compatibility
 * Returns response in Groq-like format for existing code
 */
export async function chat(
  messages: AiChatMessage[],
  _model?: string, // Model is now selected by orchestrator
  options?: {
    temperature?: number;
    max_tokens?: number;
  }
): Promise<AiChatResponseLegacy> {
  const response = await aiChat(messages, {
    temperature: options?.temperature,
    maxTokens: options?.max_tokens,
  });

  // Convert to legacy format
  return {
    id: `ai-${Date.now()}`,
    object: 'chat.completion',
    created: Math.floor(Date.now() / 1000),
    model: response.modelUsed || 'unknown',
    choices: [{
      index: 0,
      message: {
        role: 'assistant',
        content: response.content,
      },
      finish_reason: 'stop',
    }],
    usage: {
      prompt_tokens: 0,
      completion_tokens: response.tokensUsed || 0,
      total_tokens: response.tokensUsed || 0,
    },
  };
}

/**
 * Simple helper to get just the text response
 */
export async function complete(
  prompt: string,
  _model?: string, // Model is now selected by orchestrator
  systemPrompt?: string
): Promise<string> {
  const messages: AiChatMessage[] = [];

  if (systemPrompt) {
    messages.push({ role: 'system', content: systemPrompt });
  }
  messages.push({ role: 'user', content: prompt });

  const response = await aiChat(messages);
  return response.content;
}

// Export client object for drop-in replacement of groqClient
export const aiFunnelClient = {
  checkHealth: async () => {
    const health = await checkAiHealth();
    return health.available;
  },
  listModels: async () => {
    const health = await checkAiHealth();
    return health.models;
  },
  chat,
  complete,
};

export default aiFunnelClient;
